import React from "react";
import {
  Typography,
  Modal,
  Box,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EmployeesIconActive } from "@components/Icons";
import { Link } from "react-router-dom";
import ROUTES from "@config/routes";
import { useFlags } from "flagsmith/react";
import { IUser } from "@utils/interfaces";

import { RESET_USER_PASSWORD } from "@utils/featureFlags";
import { formatCNPJOrCPF } from "@utils/fiscal/helper";

interface Props {
  business: boolean;
  handle: () => void;
  user: IUser;
  loading: boolean;
  onResetPassword: (email: string) => void;
}

const paperSx = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: 2,
  p: { xs: 2, sm: "4rem 3rem" },
  width: { xs: "95vw", sm: "45rem", md: "70rem" },
  maxHeight: "90vh",
  overflow: "auto",
};

const labelSx = { fontSize: "1.2rem", fontWeight: 700, color: "#726551" };
const valueSx = {
  fontSize: "1.3rem",
  color: "#3E3D3D",
  wordBreak: "break-word" as const,
};

const ModalListBusinessName: React.FC<Props> = ({
  business,
  handle,
  user,
  loading,
  onResetPassword,
}) => {
  const flag = useFlags([RESET_USER_PASSWORD]);
  const isResetEnabled = flag.reset_user_password.enabled;

  return (
    <Modal open={business} onClose={handle}>
      <Paper sx={paperSx}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Typography variant="h5">Razões sociais deste usuário</Typography>
          <IconButton onClick={handle} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* User info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Box
            sx={{
              backgroundColor: "#FFF5F7",
              width: 48,
              height: 48,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <EmployeesIconActive />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: "1.4rem" }}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>

        {/* Desktop: table */}
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            maxHeight: "30rem",
            overflow: "auto",
            mb: 3,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Razão social</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>CNPJ ou CPF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {user.attributes.CNPJ.map((item) => (
                <TableRow key={item.CNPJ}>
                  <TableCell>{item.companyName}</TableCell>
                  <TableCell>{formatCNPJOrCPF(item.CNPJ)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Mobile: stacked cards */}
        <Box
          sx={{
            display: { xs: "block", sm: "none" },
            maxHeight: "40vh",
            overflow: "auto",
            mb: 3,
          }}
        >
          <Stack spacing={1.5}>
            {user.attributes.CNPJ.map((item) => (
              <Box
                key={item.CNPJ}
                sx={{ border: "1px solid #7265511A", borderRadius: 1, p: 1.5 }}
              >
                <Typography sx={labelSx}>Razão social</Typography>
                <Typography sx={valueSx}>{item.companyName}</Typography>
                <Typography sx={{ ...labelSx, mt: 1 }}>CNPJ ou CPF</Typography>
                <Typography sx={valueSx}>
                  {formatCNPJOrCPF(item.CNPJ)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Actions */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="center"
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            component={Link}
            to={{
              state: { user },
              pathname: ROUTES.ADMIN_ROUTES.EDIT_USER.replace(
                ":id",
                encodeURIComponent(user.id)
              ),
            }}
            sx={{ minWidth: { xs: "100%", sm: "18rem" } }}
          >
            Atualizar
          </Button>
          {isResetEnabled && (
            <Button
              variant="outlined"
              color="primary"
              size="large"
              disabled={loading}
              onClick={() => onResetPassword(user.email)}
              sx={{ minWidth: { xs: "100%", sm: "18rem" } }}
            >
              Redefinir Senha
            </Button>
          )}
        </Stack>
      </Paper>
    </Modal>
  );
};

export default ModalListBusinessName;
